import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

export type ErrorCode =
  | 'upload/invalid_file_name'
  | 'upload/file_name_too_long'
  | 'upload/file_name_missing'
  | 'upload/file_type_mismatch'
  | 'upload/file_too_small'
  | 'upload/too_many_files'
  | 'upload/max_file_size'
  | 'upload/filetype_not_supported'
  | 'upload/file_is_missing'
  | 'upload/content_type_error'

type ResponseError = { reasons?: string[]; code: string; message: string }

interface MapErrorResult {
  errorMessage: string
  reason?: ResponseError
}

export const useMapError = () => {
  const { t } = useTranslation()

  const errorCodeMapper: Record<ErrorCode, string> = {
    'upload/file_is_missing': t('errorMessage.fileMissing'),
    'upload/content_type_error': t('validation.invalidFileType', {
      fileFormats: '.jpg, .jpeg, .png'
    }),
    'upload/file_name_missing': t('errorMessage.invalidFilename'),
    'upload/file_name_too_long': t('errorMessage.invalidFilename'),
    'upload/file_too_small': t('errorMessage.invalidFileContents'),
    'upload/file_type_mismatch': t('errorMessage.fileTypeMismatch'),
    'upload/filetype_not_supported': t('validation.invalidFileType', {
      fileFormats: '.jpg, .jpeg, .png'
    }),
    'upload/invalid_file_name': t('errorMessage.invalidFilename'),
    'upload/max_file_size': t('validation.fileTooLarge', { fileSize: '2MB' }),
    'upload/too_many_files': t('errorMessage.tooManyFiles', { maxFiles: 1 })
  }

  const knownReasonMapper: Record<string, string> = {
    'body.location.link must be a valid URL': t('validation.url')
  }

  return (error: unknown): MapErrorResult => {
    const errorData = isAxiosError(error) ? (error.response?.data?.error as ResponseError) : undefined
    const mappedError = errorData ? errorCodeMapper[errorData.code as ErrorCode] : undefined

    const resolveErrorMessage = (): string => {
      if (mappedError) {
        return mappedError
      }

      if (errorData?.code === 'bad_request') {
        const firstReason = errorData.reasons?.[0]

        return (firstReason && knownReasonMapper[firstReason]) ?? firstReason ?? t('errorMessage.genericError')
      }

      return t('errorMessage.genericError')
    }

    return {
      errorMessage: resolveErrorMessage(),
      reason: errorData
    }
  }
}
